import { IconStop } from "@/assets/icons/geist/IconStop";
import { EnvironmentStatusDot } from "@/components/environments/EnvironmentStatusDot";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { Link } from "@/components/flexkit/Link";
import { TextShimmer } from "@/components/flexkit/TextShimmer";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { VisibleOnActive } from "@/components/ui/VisibleOnActive";
import { timeAgo } from "@/format/time";
import { useActiveAgentExecutions, type AgentExecutionWithContext } from "@/hooks/use-active-agent-executions";
import { useAgentExecutionActions } from "@/hooks/use-agent-execution-actions";
import { useCallbackPreventDefault } from "@/hooks/use-callback-prevent-default";
import { useProject } from "@/queries/project-queries";
import { getAgentExecutionUrl } from "@/routes/ai/ai-urls";
import { getDetailsURL } from "@/routes/environments/details-url";
import { agentExecutionLoading, agentExecutionName, canStopAgentExecution } from "@/utils/agent-executions";
import { formatError } from "@/utils/errors";
import { Timestamp, type PlainMessage } from "@bufbuild/protobuf";
import type { AgentExecution } from "gitpod-next-api/gitpod/v1/agent_pb";
import type { Environment } from "gitpod-next-api/gitpod/v1/environment_pb";
import { memo, type FC } from "react";

export const RecentAgentExecutions: FC = () => {
    const { filteredExecutions, isPending, isError } = useActiveAgentExecutions();

    const hasAgentExecutions = filteredExecutions.length > 0;
    const error = isError ? new Error("Failed to load conversations") : undefined;

    return (
        <div
            data-testid="recent-agent-executions"
            className={cn("hidden duration-300 animate-in fade-in", {
                block: !isPending,
            })}
        >
            {(hasAgentExecutions || error) && (
                <>
                    <div className="mb-4 flex justify-between">
                        <Heading2 className="text-base font-medium text-content-muted">Recent Tasks</Heading2>
                    </div>
                    <ErrorMessage error={error} data-testid="recent-agent-executions-error" />
                    <RecentAgentExecutionsList agentExecutionsWithEnvironments={filteredExecutions} />
                </>
            )}
        </div>
    );
};

const RecentAgentExecutionsList: FC<{
    agentExecutionsWithEnvironments: AgentExecutionWithContext[];
}> = ({ agentExecutionsWithEnvironments }) => {
    return (
        <ul data-testid="recent-agent-executions-list">
            {agentExecutionsWithEnvironments.map(({ execution, environment }) => (
                <AgentExecutionListItem
                    key={execution.id}
                    agentExecution={execution}
                    environment={environment}
                />
            ))}
        </ul>
    );
};

export const AgentExecutionListItem = memo(
    ({
        agentExecution,
        environment,
    }: {
        agentExecution: PlainMessage<AgentExecution>;
        environment?: PlainMessage<Environment>;
    }) => {
        const { data: project } = useProject(environment?.metadata?.projectId);

        const url = environment
            ? getDetailsURL({ environment: { id: environment.id } })
            : getAgentExecutionUrl(agentExecution.id);

        const { handleStopAgentExecution, isStopPending } = useAgentExecutionActions(agentExecution);
        const { toast } = useToast();

        const canShowStop = canStopAgentExecution(agentExecution);
        const isLoading = agentExecutionLoading(agentExecution);
        const createdAt = new Timestamp(agentExecution?.metadata?.createdAt).toDate();
        const projectName = project?.metadata?.name ?? environment?.status?.content?.git?.cloneUrl;

        const handleStop = useCallbackPreventDefault(async () => {
            try {
                await handleStopAgentExecution();
            } catch (error) {
                toast({
                    title: "Failed to stop",
                    description: formatError(error),
                });
            }
        }, [toast]);

        return (
            <li className="group flex list-none items-center justify-between border-b border-border-subtle last:border-b-0">
                <Link
                    className="mr-2 flex flex-1 items-center p-2 hover:bg-surface-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring-default focus-visible:ring-0"
                    to={url}
                >
                    {environment && (
                        <EnvironmentStatusDot size="lg" env={environment} className="m-auto size-auto pr-2" />
                    )}
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5">
                        {isLoading ? (
                            <TextShimmer
                                className="max-w-fit text-base"
                                data-testid="agent-execution-name"
                                spread={1.5}
                            >
                                {agentExecutionName(agentExecution)}
                            </TextShimmer>
                        ) : (
                            <Text
                                className="min-w-0 flex-shrink truncate text-base text-content-primary"
                                data-testid="agent-execution-name"
                            >
                                {agentExecutionName(agentExecution)}
                            </Text>
                        )}
                        <div className="flex flex-row">
                            <Text
                                className="truncate text-sm text-content-muted"
                                data-testid="agent-execution-details-started-at"
                            >
                                {timeAgo(createdAt)}
                            </Text>
                            {projectName && (
                                <Text
                                    className="truncate text-sm text-content-muted"
                                    data-testid="agent-execution-details-project-name"
                                >
                                    &nbsp;· {projectName}
                                </Text>
                            )}
                        </div>
                    </div>
                </Link>
                {canShowStop ? (
                    <VisibleOnActive>
                        <Button
                            tabIndex={0}
                            size="md"
                            variant="secondary"
                            className="focus-visible:outline-[#5E8AFD]"
                            LeadingIcon={IconStop}
                            onClick={handleStop}
                            loading={isStopPending}
                            data-tracking-id="stop-agent-execution-list-item"
                        >
                            Stop
                        </Button>
                    </VisibleOnActive>
                ) : null}
            </li>
        );
    },
);
AgentExecutionListItem.displayName = "AgentExecutionListItem";
